/**
 * sl-oe5x audit M1 — STT-batch error→i18n translator.
 *
 * The runner surfaces plan-gate / cross-tenant rejections as structured
 * `{ errorCode, errorDetails }` alongside the raw `error` string. This
 * helper resolves the code to a localised message + fills template
 * variables from `errorDetails`. Called from the execution-history panel
 * so users see a plan-specific error (with file size / limit / upgrade
 * hint) instead of a runner-internal message.
 *
 * Contract: unrecognised codes return `undefined`. Callers fall back to
 * the raw `error` string — matches the spec in
 * `STT_LARGE_FILES.md §Error surface`.
 */
import type { useI18n } from '@affine/i18n';

type I18nFn = ReturnType<typeof useI18n>;

/** Human-readable byte count (binary KiB/MiB/GiB). */
function formatBytes(bytes: unknown): string {
  const n = typeof bytes === 'number' ? bytes : Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'] as const;
  let i = 0;
  let value = n;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  const precision = value >= 10 || i === 0 ? 0 : 1;
  return `${value.toFixed(precision)} ${units[i]}`;
}

/** Human-readable duration ("90s" → "1 min 30 s"). */
function formatDurationSeconds(seconds: unknown): string {
  const n = typeof seconds === 'number' ? seconds : Number(seconds);
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n < 60) return `${Math.round(n)} s`;
  const minutes = Math.floor(n / 60);
  const secs = Math.round(n - minutes * 60);
  if (minutes < 60) {
    return secs === 0 ? `${minutes} min` : `${minutes} min ${secs} s`;
  }
  const hours = Math.floor(minutes / 60);
  const remMin = minutes - hours * 60;
  return remMin === 0 ? `${hours} h` : `${hours} h ${remMin} min`;
}

function pickNumber(
  details: Record<string, unknown> | undefined,
  ...paths: ReadonlyArray<ReadonlyArray<string>>
): number | undefined {
  if (!details) return undefined;
  for (const path of paths) {
    let cursor: unknown = details;
    for (const key of path) {
      if (cursor && typeof cursor === 'object' && key in cursor) {
        cursor = (cursor as Record<string, unknown>)[key];
      } else {
        cursor = undefined;
        break;
      }
    }
    if (typeof cursor === 'number' && Number.isFinite(cursor)) return cursor;
  }
  return undefined;
}

/**
 * Resolve a typed runner error to a localised user-facing message.
 * Returns `undefined` for unknown codes so the caller can fall back to
 * the untranslated `error` string.
 */
export function formatSttBatchError(
  t: I18nFn,
  errorCode: string | undefined,
  errorDetails: Record<string, unknown> | undefined
): string | undefined {
  if (!errorCode) return undefined;

  if (errorCode === 'STT_BATCH_FORBIDDEN') {
    return t['com.streamlain.streamlain.workflow.stt-batch.error.forbidden']();
  }

  if (errorCode !== 'STT_PLAN_LIMIT') return undefined;

  const reason = errorDetails?.['reason'];
  if (reason === 'plan_disabled') {
    return t[
      'com.streamlain.streamlain.workflow.stt-batch.error.plan-disabled'
    ]();
  }
  if (reason === 'file_too_large') {
    // Server shape (stt-batch-limits.config.ts):
    //   limit:  { maxBytes, maxDurationSeconds }
    //   actual: { bytes?, durationSeconds? }
    const actualBytes = pickNumber(errorDetails, ['actual', 'bytes']);
    const limitBytes = pickNumber(errorDetails, ['limit', 'maxBytes']);
    return t[
      'com.streamlain.streamlain.workflow.stt-batch.error.file-too-large'
    ]({
      size: formatBytes(actualBytes),
      limit: formatBytes(limitBytes),
    });
  }
  if (reason === 'duration_too_long') {
    const actualSeconds = pickNumber(errorDetails, [
      'actual',
      'durationSeconds',
    ]);
    const limitSeconds = pickNumber(errorDetails, [
      'limit',
      'maxDurationSeconds',
    ]);
    return t[
      'com.streamlain.streamlain.workflow.stt-batch.error.duration-too-long'
    ]({
      duration: formatDurationSeconds(actualSeconds),
      limit: formatDurationSeconds(limitSeconds),
    });
  }
  // sl-67kc.1.6: distinct from `plan_disabled` so the copy can point
  // the user at the "switch to sync" remediation — the plan does have
  // STT, just not the deferred-processing tier.
  if (reason === 'dynamic_not_allowed') {
    return t[
      'com.streamlain.streamlain.workflow.stt-batch.error.dynamic-not-allowed'
    ]();
  }
  return undefined;
}
